import { Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { ProtectedLayout } from '@/components/layout/ProtectedLayout';
import { RequireDashboard, RequireModule } from './guards';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { Spinner } from '@/components/ui/spinner';
import { lazyWithReload } from '@/lib/lazy-with-reload';
import { NotFoundPage } from '@/pages/error/NotFoundPage';
import { ForbiddenPage } from '@/pages/error/ForbiddenPage';
import { ServerErrorPage } from '@/pages/error/ServerErrorPage';
import { MaintenancePage } from '@/pages/error/MaintenancePage';
import { LOGGED_OUT_PATH, LOGIN_PATH, PASSWORD_LINK_NOTICE } from '@/features/auth/effeta-login';

// Lazy de páginas para code-splitting por feature. `lazyWithReload` recarga la
// pestaña una vez cuando el chunk pedido ya no existe en el servidor — pasa con
// cada deploy sobre una pestaña abierta con el build anterior.
const LoginPage = lazyWithReload(() => import('@/features/auth/pages/LoginPage'));
const LoggedOutPage = lazyWithReload(() => import('@/features/auth/pages/LoggedOutPage'));

const DashboardPage = lazyWithReload(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const BrandsListPage = lazyWithReload(() =>
  import('@/features/brands/pages/BrandsListPage').then((m) => ({
    default: m.BrandsListPage,
  })),
);
const BrandNewPage = lazyWithReload(() =>
  import('@/features/brands/pages/BrandNewPage').then((m) => ({
    default: m.BrandNewPage,
  })),
);
const BrandDetailPage = lazyWithReload(() =>
  import('@/features/brands/pages/BrandDetailPage').then((m) => ({
    default: m.BrandDetailPage,
  })),
);

const CategoriesPage = lazyWithReload(() =>
  import('@/features/categories/pages/CategoriesPage').then((m) => ({
    default: m.CategoriesPage,
  })),
);

const UsersListPage = lazyWithReload(() =>
  import('@/features/users/pages/UsersListPage').then((m) => ({
    default: m.UsersListPage,
  })),
);
const UserNewPage = lazyWithReload(() =>
  import('@/features/users/pages/UserNewPage').then((m) => ({
    default: m.UserNewPage,
  })),
);
const UserDetailPage = lazyWithReload(() =>
  import('@/features/users/pages/UserDetailPage').then((m) => ({
    default: m.UserDetailPage,
  })),
);

const IntegratorPage = lazyWithReload(() =>
  import('@/features/integrators/pages/IntegratorPage').then((m) => ({
    default: m.IntegratorPage,
  })),
);
const IntegratorsAdminPage = lazyWithReload(() =>
  import('@/features/integrators/pages/IntegratorsAdminPage').then((m) => ({
    default: m.IntegratorsAdminPage,
  })),
);
const RolesListPage = lazyWithReload(() =>
  import('@/features/roles/pages/RolesListPage').then((m) => ({
    default: m.RolesListPage,
  })),
);

const VehicleModelsListPage = lazyWithReload(() =>
  import('@/features/vehicle-models/pages/VehicleModelsListPage').then((m) => ({
    default: m.VehicleModelsListPage,
  })),
);
const VehicleModelNewPage = lazyWithReload(() =>
  import('@/features/vehicle-models/pages/VehicleModelNewPage').then((m) => ({
    default: m.VehicleModelNewPage,
  })),
);
const VehicleModelDetailPage = lazyWithReload(() =>
  import('@/features/vehicle-models/pages/VehicleModelDetailPage').then((m) => ({
    default: m.VehicleModelDetailPage,
  })),
);

const ClientsListPage = lazyWithReload(() =>
  import('@/features/admin-clients/pages/ClientsListPage').then((m) => ({
    default: m.ClientsListPage,
  })),
);
const ClientNewPage = lazyWithReload(() =>
  import('@/features/admin-clients/pages/ClientNewPage').then((m) => ({
    default: m.ClientNewPage,
  })),
);
const ClientDetailPage = lazyWithReload(() =>
  import('@/features/admin-clients/pages/ClientDetailPage').then((m) => ({
    default: m.ClientDetailPage,
  })),
);

const ProductQueryPage = lazyWithReload(() =>
  import('@/features/products-query/pages/ProductQueryPage').then((m) => ({
    default: m.ProductQueryPage,
  })),
);
const ProductsListPage = lazyWithReload(() =>
  import('@/features/products/pages/ProductsListPage').then((m) => ({
    default: m.ProductsListPage,
  })),
);
const ProductNewPage = lazyWithReload(() =>
  import('@/features/products/pages/ProductNewPage').then((m) => ({
    default: m.ProductNewPage,
  })),
);
const ProductDetailPage = lazyWithReload(() =>
  import('@/features/products/pages/ProductDetailPage').then((m) => ({
    default: m.ProductDetailPage,
  })),
);

const AiConfigsListPage = lazyWithReload(() =>
  import('@/features/ai-configs/pages/AiConfigsListPage').then((m) => ({
    default: m.AiConfigsListPage,
  })),
);
const AiConfigNewPage = lazyWithReload(() =>
  import('@/features/ai-configs/pages/AiConfigNewPage').then((m) => ({
    default: m.AiConfigNewPage,
  })),
);
const AiConfigDetailPage = lazyWithReload(() =>
  import('@/features/ai-configs/pages/AiConfigDetailPage').then((m) => ({
    default: m.AiConfigDetailPage,
  })),
);

const AiModelsPage = lazyWithReload(() =>
  import('@/features/ai-models/pages/AiModelsPage').then((m) => ({
    default: m.AiModelsPage,
  })),
);
const SuggestionsInboxPage = lazyWithReload(() =>
  import('@/features/ai-suggestions/pages/SuggestionsInboxPage').then((m) => ({
    default: m.SuggestionsInboxPage,
  })),
);
const SuggestionDetailPage = lazyWithReload(() =>
  import('@/features/ai-suggestions/pages/SuggestionDetailPage').then((m) => ({
    default: m.SuggestionDetailPage,
  })),
);

// Audit log global (admin-audit feature, endpoint GET /audit-logs).
const AuditLogsPage = lazyWithReload(() =>
  import('@/features/admin-audit/pages/AuditLogsPage').then((m) => ({
    default: m.AuditLogsPage,
  })),
);
// Versiones de un producto (feature legacy `audit-log` apuntando a
// /products/:sku/versions). Mantenemos la ruta /audit-logs/:productId como
// deeplink al historial granular.
const ProductAuditPage = lazyWithReload(() =>
  import('@/features/audit-log/pages/ProductAuditPage').then((m) => ({
    default: m.ProductAuditPage,
  })),
);

const DeliveriesListPage = lazyWithReload(() =>
  import('@/features/webhook-deliveries/pages/DeliveriesListPage').then((m) => ({
    default: m.DeliveriesListPage,
  })),
);
const DeliveryDetailPage = lazyWithReload(() =>
  import('@/features/webhook-deliveries/pages/DeliveryDetailPage').then((m) => ({
    default: m.DeliveryDetailPage,
  })),
);

const PublicCatalogPage = lazyWithReload(() =>
  import('@/features/public-catalog-preview/pages/PublicCatalogPage').then((m) => ({
    default: m.PublicCatalogPage,
  })),
);
const PublicProductPage = lazyWithReload(() =>
  import('@/features/public-catalog-preview/pages/PublicProductPage').then((m) => ({
    default: m.PublicProductPage,
  })),
);

const PendingInboxPage = lazyWithReload(() =>
  import('@/features/pending/pages/PendingInboxPage').then((m) => ({
    default: m.PendingInboxPage,
  })),
);

const IngestEventsListPage = lazyWithReload(() =>
  import('@/features/ingest-events/pages/IngestEventsListPage').then((m) => ({
    default: m.IngestEventsListPage,
  })),
);

const WebhookBackfillsListPage = lazyWithReload(() =>
  import('@/features/webhook-backfills/pages/WebhookBackfillsListPage').then((m) => ({
    default: m.WebhookBackfillsListPage,
  })),
);

const ProductImportsListPage = lazyWithReload(() =>
  import('@/features/product-imports/pages/ProductImportsListPage').then((m) => ({
    default: m.ProductImportsListPage,
  })),
);

const ActivationRulesPage = lazyWithReload(() =>
  import('@/features/activation-rules/pages/ActivationRulesPage').then((m) => ({
    default: m.ActivationRulesPage,
  })),
);

const RagReindexPage = lazyWithReload(() =>
  import('@/features/admin-rag/pages/RagReindexPage').then((m) => ({
    default: m.RagReindexPage,
  })),
);

// Sección TEMPORAL: ver `src/features/photo-studio/index.ts` para retirarla.
const PhotoStudioPage = lazyWithReload(() =>
  import('@/features/photo-studio/pages/PhotoStudioPage').then((m) => ({
    default: m.PhotoStudioPage,
  })),
);

const PageFallback = () => {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner aria-label="Cargando página" />
    </div>
  );
};

function withSuspense(node: React.ReactElement): React.ReactElement {
  return <Suspense fallback={<PageFallback />}>{node}</Suspense>;
}

const appRoutes: RouteObject[] = [
  // Públicas
  {
    path: LOGIN_PATH,
    element: withSuspense(<LoginPage />),
    handle: { breadcrumb: 'Login' },
  },
  {
    path: LOGGED_OUT_PATH,
    element: withSuspense(<LoggedOutPage />),
    handle: { breadcrumb: 'Saliste del PIM' },
  },
  // Enlace viejo del correo de contraseña (`/reset-password?token=…`): el PIM
  // ya no tiene contraseñas. Va a la entrada con un aviso y sin el token, que
  // así no queda en el historial.
  {
    path: '/reset-password',
    element: <Navigate to={`${LOGIN_PATH}?notice=${PASSWORD_LINK_NOTICE}`} replace />,
  },
  { path: '/forbidden', element: <ForbiddenPage /> },
  { path: '/maintenance', element: <MaintenancePage /> },
  { path: '/error', element: <ServerErrorPage /> },

  // Privadas (con RequireAuth + AppShell)
  {
    element: <ProtectedLayout />,
    children: [
      // Sin tarjetas de acción para el user, `RequireDashboard` lo manda a su
      // primera sección (ver `useHomeTarget`).
      {
        path: '/',
        element: <RequireDashboard>{withSuspense(<DashboardPage />)}</RequireDashboard>,
        handle: { breadcrumb: 'Inicio' },
      },

      // Módulos: `RequireModule` aplica los permisos del registro
      // (`src/config/modules.ts`), los mismos que deciden el sidebar. Las
      // rutas hijas (`new`, `:id`) heredan ese requisito: cada módulo ya pide
      // la escritura que necesita su alta.

      // Brands (CRUD real implementado)
      {
        path: '/brands',
        element: (
          <RequireModule module="brands">
            <Outlet />
          </RequireModule>
        ),
        handle: { breadcrumb: 'Marcas' },
        children: [
          { index: true, element: withSuspense(<BrandsListPage />) },
          {
            path: 'new',
            element: withSuspense(<BrandNewPage />),
            handle: { breadcrumb: 'Nueva' },
          },
          {
            path: ':id',
            element: withSuspense(<BrandDetailPage />),
            handle: { breadcrumb: 'Detalle' },
          },
        ],
      },

      // Categories
      {
        path: '/categories',
        element: (
          <RequireModule module="categories">{withSuspense(<CategoriesPage />)}</RequireModule>
        ),
        handle: { breadcrumb: 'Categorías' },
      },

      // Users
      {
        path: '/users',
        element: (
          <RequireModule module="users">
            <Outlet />
          </RequireModule>
        ),
        handle: { breadcrumb: 'Usuarios' },
        children: [
          { index: true, element: withSuspense(<UsersListPage />) },
          {
            path: 'new',
            element: withSuspense(<UserNewPage />),
            handle: { breadcrumb: 'Nuevo' },
          },
          {
            path: ':id',
            element: withSuspense(<UserDetailPage />),
            handle: { breadcrumb: 'Detalle' },
          },
        ],
      },

      // Roles (release 2026-08-24). Gate por permission alineado con back.
      {
        path: '/roles',
        handle: { breadcrumb: 'Roles' },
        element: <RequireModule module="roles">{withSuspense(<RolesListPage />)}</RequireModule>,
      },

      // Integradores admin (release 2026-08-26). CRUD gated por
      // `integrations:manage`. La ruta user-facing (`/integrators/:slug`) no
      // usa RequirePermission — el gate real es la membership: `useIntegrators`
      // solo devuelve integradores del role del caller, y IntegratorPage
      // rechaza al que no matchee. Sin permission-per-slug para no forzar
      // deploy de la const cada vez que se suma un partner.
      {
        path: '/admin/integrators',
        handle: { breadcrumb: 'Integradores' },
        element: (
          <RequireModule module="integrators">
            {withSuspense(<IntegratorsAdminPage />)}
          </RequireModule>
        ),
      },
      {
        path: '/integrators/:slug',
        handle: { breadcrumb: 'Integrador' },
        element: withSuspense(<IntegratorPage />),
      },

      // Vehicle Models
      {
        path: '/vehicle-models',
        element: (
          <RequireModule module="vehicleModels">
            <Outlet />
          </RequireModule>
        ),
        handle: { breadcrumb: 'Modelos' },
        children: [
          { index: true, element: withSuspense(<VehicleModelsListPage />) },
          {
            path: 'new',
            element: withSuspense(<VehicleModelNewPage />),
            handle: { breadcrumb: 'Nuevo' },
          },
          {
            path: ':id',
            element: withSuspense(<VehicleModelDetailPage />),
            handle: { breadcrumb: 'Detalle' },
          },
        ],
      },

      // Admin Clients (B2B)
      {
        path: '/admin-clients',
        element: (
          <RequireModule module="adminClients">
            <Outlet />
          </RequireModule>
        ),
        handle: { breadcrumb: 'Clientes B2B' },
        children: [
          { index: true, element: withSuspense(<ClientsListPage />) },
          {
            path: 'new',
            element: withSuspense(<ClientNewPage />),
            handle: { breadcrumb: 'Nuevo' },
          },
          {
            path: ':id',
            element: withSuspense(<ClientDetailPage />),
            handle: { breadcrumb: 'Detalle' },
          },
        ],
      },

      // Query Lab. Ruta propia, no hija de /products: es otro módulo (no pide
      // `products:write`) y así no hereda el crumb "Productos", que linkea a
      // una pantalla que su rol quizá no abre. React Router rankea el segmento
      // estático `query` por encima de `/products/:sku`.
      {
        path: '/products/query',
        element: (
          <RequireModule module="queryLab">{withSuspense(<ProductQueryPage />)}</RequireModule>
        ),
        handle: { breadcrumb: 'Query Lab' },
      },

      // Products
      {
        path: '/products',
        element: (
          <RequireModule module="products">
            <Outlet />
          </RequireModule>
        ),
        handle: { breadcrumb: 'Productos' },
        children: [
          { index: true, element: withSuspense(<ProductsListPage />) },
          {
            path: 'new',
            element: withSuspense(<ProductNewPage />),
            handle: { breadcrumb: 'Nuevo' },
          },
          {
            path: ':sku',
            element: withSuspense(<ProductDetailPage />),
            handle: { breadcrumb: 'Detalle' },
          },
        ],
      },

      // Catálogo de modelos IA. Alimenta el desplegable de /ai-configs y es el
      // allowlist contra el que el back valida `ai_attribute_config.model`.
      {
        path: '/ai-models',
        element: (
          <RequireModule module="aiModels">
            <Outlet />
          </RequireModule>
        ),
        handle: { breadcrumb: 'Modelos de IA' },
        children: [{ index: true, element: withSuspense(<AiModelsPage />) }],
      },

      // AI Configs
      {
        path: '/ai-configs',
        element: (
          <RequireModule module="aiConfigs">
            <Outlet />
          </RequireModule>
        ),
        handle: { breadcrumb: 'Configuración IA' },
        children: [
          { index: true, element: withSuspense(<AiConfigsListPage />) },
          {
            path: 'new',
            element: withSuspense(<AiConfigNewPage />),
            handle: { breadcrumb: 'Nueva' },
          },
          {
            path: ':id',
            element: withSuspense(<AiConfigDetailPage />),
            handle: { breadcrumb: 'Detalle' },
          },
        ],
      },

      // AI Suggestions
      {
        path: '/ai-suggestions',
        element: (
          <RequireModule module="aiSuggestions">
            <Outlet />
          </RequireModule>
        ),
        handle: { breadcrumb: 'Sugerencias IA' },
        children: [
          { index: true, element: withSuspense(<SuggestionsInboxPage />) },
          {
            path: ':id',
            element: withSuspense(<SuggestionDetailPage />),
            handle: { breadcrumb: 'Detalle' },
          },
        ],
      },

      // Pending inbox
      {
        path: '/pending',
        element: (
          <RequireModule module="pending">{withSuspense(<PendingInboxPage />)}</RequireModule>
        ),
        handle: { breadcrumb: 'Pendientes' },
      },

      // Audit log
      {
        path: '/audit-logs',
        element: (
          <RequireModule module="auditLogs">
            <Outlet />
          </RequireModule>
        ),
        handle: { breadcrumb: 'Registro de cambios' },
        children: [
          { index: true, element: withSuspense(<AuditLogsPage />) },
          {
            path: ':productId',
            element: withSuspense(<ProductAuditPage />),
            handle: { breadcrumb: 'Producto' },
          },
        ],
      },

      // Eventos de ingesta admin. Retry gate a nivel de botón + back.
      {
        path: '/admin/ingest-events',
        element: (
          <RequireModule module="ingestEvents">
            {withSuspense(<IngestEventsListPage />)}
          </RequireModule>
        ),
        handle: { breadcrumb: 'Eventos de ingesta' },
      },

      // Backfills de webhooks — empujar productos a clientes B2B.
      {
        path: '/admin/webhook-backfills',
        element: (
          <RequireModule module="webhookBackfills">
            {withSuspense(<WebhookBackfillsListPage />)}
          </RequireModule>
        ),
        handle: { breadcrumb: 'Reenviar notificaciones' },
      },

      // Importaciones masivas de productos (release hub-service-00164-kxz).
      // El listado + drawer + upload viven acá. Permission `products:write`
      // porque cada job crea/actualiza productos en lote.
      {
        path: '/admin/product-imports',
        element: (
          <RequireModule module="productImports">
            {withSuspense(<ProductImportsListPage />)}
          </RequireModule>
        ),
        handle: { breadcrumb: 'Importaciones de productos' },
      },

      // Reglas de activación: globales, deciden si un producto puede estar
      // activo. Simular, activar y desactivar corren como barridos.
      {
        path: '/admin/activation-rules',
        element: (
          <RequireModule module="activationRules">
            {withSuspense(<ActivationRulesPage />)}
          </RequireModule>
        ),
        handle: { breadcrumb: 'Reglas de activación' },
      },

      // RAG reindex manual
      {
        path: '/admin/rag/reindex',
        element: (
          <RequireModule module="ragReindex">{withSuspense(<RagReindexPage />)}</RequireModule>
        ),
        handle: { breadcrumb: 'Reindexar RAG' },
      },

      // Estudio de fotos (TEMPORAL).
      {
        path: '/photo-studio',
        element: (
          <RequireModule module="photoStudio">{withSuspense(<PhotoStudioPage />)}</RequireModule>
        ),
        handle: { breadcrumb: 'Estudio de fotos' },
      },

      // Webhook deliveries
      {
        path: '/webhook-deliveries',
        element: (
          <RequireModule module="webhookDeliveries">
            <Outlet />
          </RequireModule>
        ),
        handle: { breadcrumb: 'Notificaciones enviadas' },
        children: [
          { index: true, element: withSuspense(<DeliveriesListPage />) },
          {
            path: ':id',
            element: withSuspense(<DeliveryDetailPage />),
            handle: { breadcrumb: 'Detalle' },
          },
        ],
      },

      // Public catalog preview
      {
        path: '/public-preview',
        handle: { breadcrumb: 'Catálogo público' },
        children: [
          { index: true, element: withSuspense(<PublicCatalogPage />) },
          {
            path: ':sku',
            element: withSuspense(<PublicProductPage />),
            handle: { breadcrumb: 'Producto' },
          },
        ],
      },
    ],
  },

  // Catch-all 404
  { path: '*', element: <Navigate to="/404" replace /> },
  { path: '/404', element: <NotFoundPage /> },
];

// Exportado para tests de guards sobre la config real (`router.test.tsx`).
// Todo el árbol cuelga de una raíz sin `path`: los paths hijos siguen siendo
// absolutos (mismo patrón que la ruta de `ProtectedLayout`) y el
// `errorElement` pasa a cubrir cualquier ruta, incluidas las públicas.
export const routes: RouteObject[] = [
  {
    element: <Outlet />,
    errorElement: <RouteErrorBoundary />,
    children: appRoutes,
  },
];

export const router = createBrowserRouter(routes);
